// mission-state-machine.ts
//
// Pure 12-phase x 9-execution_status transition table (spec §4). No I/O, no
// Date.now() — the caller (supervisor/gate-registry/directive-inbox) decides
// WHEN to transition; this module only decides whether a proposed transition
// is LEGAL and whether dispatch is allowed in the current state.
//
// Enums imported READ-ONLY from mission-schema.ts (P4, do not redefine).

import type { z } from "zod";
import type { MissionExecutionStatusSchema, MissionPhaseSchema } from "./schemas/mission-schema.js";

export type MissionPhase = z.infer<typeof MissionPhaseSchema>;
export type MissionExecutionStatus = z.infer<typeof MissionExecutionStatusSchema>;

export interface MissionMachineState {
  phase: MissionPhase;
  executionStatus: MissionExecutionStatus;
}

export class IllegalTransitionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IllegalTransitionError";
  }
}

// Linear phase order per spec §4:
// DRAFTING → SURVEYING* → PLANNING → DELIVERING → TESTING ↔ FIXING → REVIEWING
// → DEPLOYING → VERIFYING → DONE, with ROLLING_BACK reachable from VERIFYING
// (approved recovery plan) and ABORTED reachable from any non-terminal phase.
const PHASE_ORDER: MissionPhase[] = [
  "DRAFTING",
  "SURVEYING",
  "PLANNING",
  "DELIVERING",
  "TESTING",
  "FIXING",
  "REVIEWING",
  "DEPLOYING",
  "VERIFYING",
  "ROLLING_BACK",
  "DONE",
  "ABORTED",
];

const TERMINAL_PHASES: ReadonlySet<MissionPhase> = new Set(["DONE", "ABORTED"]);

// Adjacency: which phases a given phase may move to directly. TESTING <-> FIXING
// is bidirectional (regression cycle, §5.2). SURVEYING is brownfield-only but the
// transition itself is still legal here — "mode" gating is the caller's job, not
// this pure table's.
const PHASE_TRANSITIONS: Record<MissionPhase, MissionPhase[]> = {
  DRAFTING: ["SURVEYING", "PLANNING", "ABORTED"],
  SURVEYING: ["PLANNING", "ABORTED"],
  PLANNING: ["DELIVERING", "ABORTED"],
  DELIVERING: ["TESTING", "ABORTED"],
  TESTING: ["FIXING", "REVIEWING", "ABORTED"],
  FIXING: ["TESTING", "ABORTED"],
  REVIEWING: ["DEPLOYING", "TESTING", "ABORTED"],
  DEPLOYING: ["VERIFYING", "ABORTED"],
  VERIFYING: ["DONE", "ROLLING_BACK", "ABORTED"],
  ROLLING_BACK: ["ABORTED", "VERIFYING"],
  DONE: [],
  ABORTED: [],
};

export function isTerminalPhase(phase: MissionPhase): boolean {
  return TERMINAL_PHASES.has(phase);
}

export function isTerminalExecutionStatus(status: MissionExecutionStatus): boolean {
  return status === "TERMINAL";
}

/** Whether `next` is a legal phase transition from `current`. Same phase is always legal (no-op). */
export function isLegalPhaseTransition(current: MissionPhase, next: MissionPhase): boolean {
  if (current === next) return true;
  return PHASE_TRANSITIONS[current]?.includes(next) ?? false;
}

/**
 * Apply a phase transition. Throws IllegalTransitionError for a move not in the
 * table — callers (supervisor) must not silently coerce an illegal move.
 */
export function transitionPhase(
  state: MissionMachineState,
  nextPhase: MissionPhase,
): MissionMachineState {
  if (isTerminalPhase(state.phase)) {
    throw new IllegalTransitionError(
      `Mission ở phase TERMINAL "${state.phase}" — không thể chuyển sang "${nextPhase}".`,
    );
  }
  if (!isLegalPhaseTransition(state.phase, nextPhase)) {
    throw new IllegalTransitionError(
      `Chuyển phase không hợp lệ: "${state.phase}" → "${nextPhase}". ` +
        `Các đích hợp lệ từ "${state.phase}": ${PHASE_TRANSITIONS[state.phase]?.join(", ") || "(không có)"}.`,
    );
  }
  return { ...state, phase: nextPhase };
}

/**
 * Whether dispatch (spawning a new worker/controller operation) is allowed given
 * the current state. Terminal phase OR TERMINAL execution_status both refuse
 * dispatch (spec §4 execution_status table: "TERMINAL ... không dispatch").
 */
export function canDispatch(state: MissionMachineState): boolean {
  if (isTerminalPhase(state.phase)) return false;
  if (isTerminalExecutionStatus(state.executionStatus)) return false;
  return state.executionStatus === "READY";
}

export function assertCanDispatch(state: MissionMachineState): void {
  if (!canDispatch(state)) {
    throw new IllegalTransitionError(
      `Không thể dispatch: phase="${state.phase}" execution_status="${state.executionStatus}".`,
    );
  }
}

export { PHASE_ORDER };
